import { forwardRef, type ImgHTMLAttributes, useRef, useEffect, useState, useImperativeHandle } from 'react'
import { cn } from '#app/utils/tailwind-merge'

export type ImageProps = ImgHTMLAttributes<HTMLImageElement> & {
	alt: string
	src: string
	width: string
	height: string
}

const Img = forwardRef<HTMLImageElement, ImageProps>(({ src, alt, width, height, className, ...props }, ref) => {
	const [state, setState] = useState<'loading' | 'loaded' | 'failed' | null>(null)
	const imageRef = useRef<HTMLImageElement>(null)
	useImperativeHandle<HTMLImageElement | null, HTMLImageElement | null>(ref, () => imageRef.current) // Merge the two refs

	useEffect(() => {
		setState('loading') // Enable loading state
		// If JS loads before image (otherwise rely on onLoad/onError events)
		if (imageRef.current && imageRef.current.complete) {
			const success = imageRef.current.naturalWidth > 0
			if (success) {
				onLoad()
			} else {
				onError()
			}
		}
	}, [imageRef])

	const onLoad = () => {
		setState('loaded')
	}
	const onError = () => {
		setState('failed')
	}

	return (
		<img
			ref={imageRef}
			width={width}
			height={height}
			onLoad={onLoad}
			onError={onError}
			data-loading={state === 'loading' || undefined}
			data-loaded={state === 'loaded' || undefined}
			data-failed={state === 'failed' || undefined}
			src={src}
			alt={alt}
			{...props}
			className={cn('image', className)}
		/>
	)
})
Img.displayName = 'Img'
export { Img }
